import { supabase } from './supabaseClient';
import { uploadToCloudinary } from './cloudinaryUpload';
import { 
  uploadVideoToVimeo, 
  makeVimeoVideoPublic, 
  isCloudinaryVideo,
  extractVimeoId 
} from './vimeoApi';

/**
 * Pulse Admin API Functions
 * Direct SQL queries for pulse post management
 */

/**
 * Fetch all pulse posts with user info and pagination
 * @param {Object} filters - { status, post_type, user_id, search, date_from, date_to }
 * @param {number} limit - Number of posts per page
 * @param {number} offset - Offset for pagination
 */
export const getAllPulsePosts = async (filters = {}, limit = 20, offset = 0) => {
  try {
    let query = supabase
      .from('pulse')
      .select(`
        id,
        created_at,
        updated_at,
        text_content,
        source,
        media_type,
        media_source,
        status,
        post_type,
        target_type,
        target_url,
        vc_id,
        gallery_id,
        collection_id,
        event_id,
        needs_approval,
        approved,
        user_id,
        users!pulse_user_id_fkey (
          uuid,
          nickname,
          handle,
          profile_img,
          email
        )
      `)
      .order('created_at', { ascending: false })
      .range(offset, offset + limit - 1);

    // Apply filters
    if (filters.status) {
      query = query.eq('status', filters.status);
    }
    
    if (filters.media_type) {
      if (filters.media_type === 'none' || filters.media_type === 'text') {
        query = query.is('media_type', null);
      } else {
        query = query.eq('media_type', filters.media_type);
      }
    }
    
    if (filters.post_type) {
      query = query.eq('post_type', filters.post_type);
    }
    
    if (filters.user_id) {
      query = query.eq('user_id', filters.user_id);
    }
    
    if (filters.search) {
      query = query.ilike('text_content', `%${filters.search}%`);
    }
    
    if (filters.date_from) {
      query = query.gte('created_at', filters.date_from);
    }
    
    if (filters.date_to) {
      query = query.lte('created_at', filters.date_to);
    }

    const { data, error, count } = await query;

    if (error) throw error;

    // Get total count for pagination
    const { count: totalCount, error: countError } = await supabase
      .from('pulse')
      .select('*', { count: 'exact', head: true });

    return {
      posts: data || [],
      total: totalCount || 0,
      hasMore: (offset + limit) < (totalCount || 0)
    };
  } catch (error) {
    console.error('Error fetching pulse posts:', error);
    throw error;
  }
};

/**
 * Get posts needing moderation
 */
export const getModerationQueue = async () => {
  try {
    const { data, error } = await supabase
      .from('pulse')
      .select(`
        id,
        created_at,
        text_content,
        source,
        media_type,
        status,
        post_type,
        user_id,
        users!pulse_user_id_fkey (
          uuid,
          nickname,
          handle,
          profile_img,
          email
        )
      `)
      .eq('needs_approval', true)
      .eq('approved', false)
      .order('created_at', { ascending: true });

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error fetching moderation queue:', error);
    throw error;
  }
};

/**
 * Create a new pulse post
 * @param {Object} postData - Pulse post data
 */
export const createPulsePost = async (postData) => {
  try {
    const { data, error } = await supabase
      .from('pulse')
      .insert([{
        text_content: postData.text_content,
        source: postData.source,
        media_type: postData.media_type,
        media_source: postData.media_source || 'upload',
        status: postData.status || 'Public',
        post_type: postData.post_type || 'other',
        target_type: postData.target_type,
        target_url: postData.target_url,
        user_id: postData.user_id,
        vc_id: postData.vc_id || null,
        gallery_id: postData.gallery_id || null,
        collection_id: postData.collection_id || null,
        event_id: postData.event_id || null,
        needs_approval: postData.needs_approval || false,
        approved: postData.approved !== undefined ? postData.approved : true
      }])
      .select(`
        id,
        created_at,
        text_content,
        source,
        media_type,
        status,
        post_type,
        user_id,
        users!pulse_user_id_fkey (
          uuid,
          nickname,
          handle,
          profile_img
        )
      `)
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error creating pulse post:', error);
    throw error;
  }
};

/**
 * Update a pulse post
 * @param {number} postId - Post ID
 * @param {Object} updates - Fields to update
 */
export const updatePulsePost = async (postId, updates) => {
  try {
    const { data, error } = await supabase
      .from('pulse')
      .update({
        ...updates,
        updated_at: new Date().toISOString()
      })
      .eq('id', postId)
      .select(`
        id,
        created_at,
        updated_at,
        text_content,
        source,
        media_type,
        status,
        post_type,
        approved,
        user_id
      `)
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error updating pulse post:', error);
    throw error;
  }
};

/**
 * Delete a pulse post
 * @param {number} postId - Post ID
 */
export const deletePulsePost = async (postId) => {
  try {
    const { error } = await supabase
      .from('pulse')
      .delete()
      .eq('id', postId);

    if (error) throw error;
    return true;
  } catch (error) {
    console.error('Error deleting pulse post:', error);
    throw error;
  }
};

/**
 * Approve a pulse post
 * @param {number} postId - Post ID
 */
export const approvePulsePost = async (postId) => {
  try {
    const { data, error } = await supabase
      .from('pulse')
      .update({
        approved: true,
        needs_approval: false,
        updated_at: new Date().toISOString()
      })
      .eq('id', postId)
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error approving pulse post:', error);
    throw error;
  }
};

/**
 * Reject a pulse post (set to private)
 * @param {number} postId - Post ID
 */
export const rejectPulsePost = async (postId) => {
  try {
    const { data, error } = await supabase
      .from('pulse')
      .update({
        approved: false,
        status: 'Private',
        updated_at: new Date().toISOString()
      })
      .eq('id', postId)
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error rejecting pulse post:', error);
    throw error;
  }
};

/**
 * Get pulse analytics
 */
export const getPulseAnalytics = async () => {
  try {
    // Total posts
    const { count: totalPosts } = await supabase
      .from('pulse')
      .select('*', { count: 'exact', head: true });

    // Posts by status
    const { data: statusData } = await supabase
      .from('pulse')
      .select('status')
      .select('status', { count: 'exact' });

    // Posts needing approval
    const { count: pendingApproval } = await supabase
      .from('pulse')
      .select('*', { count: 'exact', head: true })
      .eq('needs_approval', true)
      .eq('approved', false);

    // Posts by type
    const { data: typeData } = await supabase
      .from('pulse')
      .select('post_type');

    // Recent activity (last 7 days)
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
    
    const { count: recentPosts } = await supabase
      .from('pulse')
      .select('*', { count: 'exact', head: true })
      .gte('created_at', sevenDaysAgo.toISOString());

    // Count by post type
    const postTypeCount = {};
    typeData?.forEach(item => {
      postTypeCount[item.post_type] = (postTypeCount[item.post_type] || 0) + 1;
    });

    return {
      totalPosts: totalPosts || 0,
      pendingApproval: pendingApproval || 0,
      recentPosts: recentPosts || 0,
      postTypeCount
    };
  } catch (error) {
    console.error('Error fetching pulse analytics:', error);
    throw error;
  }
};

/**
 * Search users for post creation (post on behalf of)
 * @param {string} searchQuery - Search term
 */
export const searchUsers = async (searchQuery) => {
  try {
    const { data, error } = await supabase
      .from('users')
      .select('uuid, nickname, handle, profile_img, email')
      .or(`nickname.ilike.%${searchQuery}%,handle.ilike.%${searchQuery}%,email.ilike.%${searchQuery}%`)
      .limit(10);

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error searching users:', error);
    throw error;
  }
};

/**
 * Get user's artworks for portfolio selection
 * @param {string} userId - User UUID
 */
export const getUserArtworks = async (userId) => {
  try {
    const { data, error } = await supabase
      .from('vcs')
      .select('vc_id, title, cover_image, artist_nickname')
      .eq('user_uuid', userId)
      .order('created_at', { ascending: false })
      .limit(50);

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error fetching user artworks:', error);
    throw error;
  }
};

/**
 * Upload media (image/video) to Cloudinary for pulse posts
 * @param {File} file - The file to upload
 * @param {string} mediaType - 'image' or 'video'
 * @returns {Promise<{url: string, publicId: string}>}
 */
export const uploadPulseMedia = async (file, mediaType = 'image') => {
  try {
    // Determine resource type and folder
    const resourceType = mediaType === 'video' ? 'video' : 'image';
    const folder = 'pulse';
    
    // Upload to Cloudinary using the existing upload function
    const uploadResult = await uploadToCloudinary(file, resourceType, folder);
    
    return {
      url: uploadResult.url,
      publicId: uploadResult.publicId,
      format: uploadResult.format,
      bytes: uploadResult.bytes,
      duration: uploadResult.duration // For videos
    };
  } catch (error) {
    console.error('Error uploading pulse media:', error);
    throw error;
  }
};

/**
 * Transfer video from Cloudinary to Vimeo
 * @param {number} postId - Pulse post ID
 * @returns {Promise<Object>} Updated post with Vimeo URL
 */
export const transferVideoToVimeo = async (postId) => {
  try {
    console.log('🎬 Starting transfer to Vimeo for post:', postId);

    // Get current post
    const { data: post, error: fetchError } = await supabase
      .from('pulse')
      .select(`
        *,
        users!pulse_user_id_fkey (
          uuid,
          nickname,
          handle,
          email
        )
      `)
      .eq('id', postId)
      .single();

    if (fetchError) throw fetchError;

    // Validation
    if (post.media_type !== 'video') {
      throw new Error('Post is not a video - cannot transfer to Vimeo');
    }

    if (!isCloudinaryVideo(post.source)) {
      throw new Error('Video is not hosted on Cloudinary - already on Vimeo or external source');
    }

    // Upload to Vimeo using pull upload
    console.log('📤 Uploading to Vimeo from:', post.source);
    const vimeoData = await uploadVideoToVimeo(post.source, {
      title: post.text_content?.substring(0, 128) || `Pulse by ${post.users?.nickname || post.users?.handle}`,
      description: `VibrantCast Pulse Post\nPosted by: ${post.users?.nickname || post.users?.handle}\nOriginal post ID: ${postId}`
    });

    console.log('✅ Vimeo upload complete:', vimeoData.id);

    // Update pulse record with Vimeo URL
    const { data: updated, error: updateError } = await supabase
      .from('pulse')
      .update({
        source: vimeoData.player_url,
        updated_at: new Date().toISOString()
      })
      .eq('id', postId)
      .select(`
        *,
        users!pulse_user_id_fkey (
          uuid,
          nickname,
          handle,
          profile_img,
          email
        )
      `)
      .single();

    if (updateError) throw updateError;

    console.log('✅ Post updated with Vimeo URL');

    return {
      ...updated,
      vimeo_id: vimeoData.id,
      vimeo_uri: vimeoData.uri,
      old_cloudinary_url: post.source // Return old URL for optional cleanup
    };

  } catch (error) {
    console.error('❌ Transfer to Vimeo error:', error);
    throw error;
  }
};

/**
 * Approve post and auto-transfer video to Vimeo if it's on Cloudinary
 * @param {number} postId - Post ID to approve
 * @returns {Promise<Object>} Approved post (with Vimeo URL if video was transferred)
 */
export const approvePulsePostWithVimeo = async (postId) => {
  try {
    console.log('✅ Approving post:', postId);

    // First get the post to check if it's a video
    const { data: post, error: fetchError } = await supabase
      .from('pulse')
      .select('id, media_type, source, status')
      .eq('id', postId)
      .single();

    if (fetchError) throw fetchError;

    // Approve the post first
    const { data: approved, error: approveError } = await supabase
      .from('pulse')
      .update({
        approved: true,
        needs_approval: false,
        status: 'Public',
        updated_at: new Date().toISOString()
      })
      .eq('id', postId)
      .select(`
        *,
        users!pulse_user_id_fkey (
          uuid,
          nickname,
          handle,
          profile_img,
          email
        )
      `)
      .single();

    if (approveError) throw approveError;

    // If it's a video on Cloudinary, transfer to Vimeo
    if (post.media_type === 'video' && isCloudinaryVideo(post.source)) {
      console.log('🎥 Detected Cloudinary video - auto-transferring to Vimeo...');
      
      try {
        const transferred = await transferVideoToVimeo(postId);
        
        // Make the video public on Vimeo
        const vimeoId = extractVimeoId(transferred.source);
        if (vimeoId) {
          await makeVimeoVideoPublic(vimeoId);
          console.log('🔓 Vimeo video set to public');
        }
        
        return transferred;
      } catch (vimeoError) {
        console.error('⚠️ Vimeo transfer failed, but post is still approved:', vimeoError);
        // Post is still approved even if Vimeo transfer fails
        // Admin can manually transfer later
        return {
          ...approved,
          vimeo_transfer_error: vimeoError.message
        };
      }
    }

    console.log('✅ Post approved (no Vimeo transfer needed)');
    return approved;

  } catch (error) {
    console.error('❌ Approve with Vimeo error:', error);
    throw error;
  }
};

/**
 * Check post transfer status - useful for monitoring
 * @param {number} postId - Post ID
 * @returns {Promise<Object>} Status information
 */
export const getPostTransferStatus = async (postId) => {
  try {
    const { data: post, error } = await supabase
      .from('pulse')
      .select('id, media_type, source, status, approved')
      .eq('id', postId)
      .single();

    if (error) throw error;

    const isVideo = post.media_type === 'video';
    const onCloudinary = isCloudinaryVideo(post.source);
    const vimeoId = extractVimeoId(post.source);
    
    return {
      postId: post.id,
      isVideo,
      onCloudinary,
      onVimeo: !!vimeoId,
      vimeoId,
      canTransfer: isVideo && onCloudinary,
      needsTransfer: isVideo && onCloudinary && post.approved,
      currentSource: post.source,
      status: post.status,
      approved: post.approved
    };

  } catch (error) {
    console.error('Error checking transfer status:', error);
    throw error;
  }
};
